import React, { useState } from "react";
import {
  Drawer,
  Typography,
  Button,
  Box,
  IconButton,
  TextField,
  InputAdornment,
} from "@mui/material";
import { 
  ArrowBack as BackIcon, 
  Close as CloseIcon,
  Visibility,
  VisibilityOff
} from "@mui/icons-material";

function GitHubConfigDrawer({ 
  open, 
  onClose, 
  onConfirm,
  isLoading = false
}) {
  const [formData, setFormData] = useState({
    username: "",
    token: "",
    repoName: ""
  });
  const [showToken, setShowToken] = useState(false);
  const [errors, setErrors] = useState({});

  // Handle input changes
  const handleInputChange = (field) => (event) => {
    const value = event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ""
      }));
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.username.trim()) {
      newErrors.username = "Username is required";
    }
    
    if (!formData.token.trim()) {
      newErrors.token = "Token is required";
    }
    
    if (!formData.repoName.trim()) {
      newErrors.repoName = "Repository name is required";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle confirm button click
  const handleConfirm = () => {
    if (validateForm()) {
      onConfirm(formData);
    }
  };

  // Handle back button click
  const handleBack = () => {
    onClose();
  };

  // Handle close button click
  const handleClose = () => {
    onClose();
  };

  // Toggle token visibility
  const toggleTokenVisibility = () => {
    setShowToken(!showToken);
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      slotProps={{
        paper: {
          sx: {
            width: {
              xs: "90vw", // Mobile
              sm: "70vw", // Tablet
              md: "50vw", // Small laptops
              lg: "40vw", // Desktop
            },
            maxWidth: "600px",
            borderRadius: "8px 0 0 8px",
          },
        },
      }}
    >
      <Box
        sx={{
          p: { xs: 2, sm: 3 },
          display: "flex",
          flexDirection: "column",
          height: "100%",
          gap: "24px",
        }}
      >
        {/* Header */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IconButton
              onClick={handleBack}
              sx={{
                color: "#0094FB",
                "&:hover": {
                  backgroundColor: "rgba(0, 148, 251, 0.04)",
                },
              }}
            >
              <BackIcon />
            </IconButton>
            <Typography
              variant="h6"
              sx={{
                fontFamily: "Manrope",
                fontWeight: 700,
                fontSize: "20px",
                color: "#000000",
              }}
            >
              GitHub Configuration
            </Typography>
          </Box>
          
          <IconButton
            onClick={handleClose}
            sx={{
              color: "#666",
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.04)",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: "24px" }}>
          {/* Username Field */}
          <Box>
            <Typography
              variant="body2"
              sx={{
                fontFamily: "Manrope",
                fontWeight: 600,
                fontSize: "14px",
                color: "#000000",
                mb: 1,
              }}
            >
              Username
            </Typography>
            <TextField
              fullWidth
              placeholder="Enter Username"
              value={formData.username}
              onChange={handleInputChange("username")}
              error={!!errors.username}
              helperText={errors.username}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  fontFamily: "Manrope",
                  fontSize: "14px",
                  "& fieldset": {
                    borderColor: "#E2E2E2",
                  },
                  "&:hover fieldset": {
                    borderColor: "#0094FB",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#0094FB",
                    borderWidth: "2px",
                  },
                },
                "& .MuiInputBase-input": {
                  padding: "12px 16px",
                },
              }}
            />
          </Box>

          {/* Token Field */}
          <Box>
            <Typography
              variant="body2"
              sx={{
                fontFamily: "Manrope",
                fontWeight: 600,
                fontSize: "14px",
                color: "#000000",
                mb: 1,
              }}
            >
              Token
            </Typography>
            <TextField
              fullWidth
              type={showToken ? "text" : "password"}
              placeholder="Enter Token/key"
              value={formData.token}
              onChange={handleInputChange("token")}
              error={!!errors.token}
              helperText={errors.token}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={toggleTokenVisibility}
                      edge="end"
                      sx={{
                        color: "#666",
                        "&:hover": {
                          backgroundColor: "rgba(0, 0, 0, 0.04)",
                        },
                      }}
                    >
                      {showToken ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  fontFamily: "Manrope",
                  fontSize: "14px",
                  "& fieldset": {
                    borderColor: "#E2E2E2",
                  },
                  "&:hover fieldset": {
                    borderColor: "#0094FB",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#0094FB",
                    borderWidth: "2px",
                  },
                },
                "& .MuiInputBase-input": {
                  padding: "12px 16px",
                },
              }}
            />
          </Box>

          {/* Repository Name Field */}
          <Box>
            <Typography
              variant="body2"
              sx={{
                fontFamily: "Manrope",
                fontWeight: 600,
                fontSize: "14px",
                color: "#000000",
                mb: 1,
              }}
            >
              Repo Name
            </Typography>
            <TextField
              fullWidth
              placeholder="Enter Repo Name"
              value={formData.repoName}
              onChange={handleInputChange("repoName")}
              error={!!errors.repoName}
              helperText={errors.repoName}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  fontFamily: "Manrope",
                  fontSize: "14px",
                  "& fieldset": {
                    borderColor: "#E2E2E2",
                  },
                  "&:hover fieldset": {
                    borderColor: "#0094FB",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#0094FB",
                    borderWidth: "2px",
                  },
                },
                "& .MuiInputBase-input": {
                  padding: "12px 16px",
                },
              }}
            />
          </Box>
        </Box>

        {/* Footer Buttons */}
        
        <Box sx={{ display: "flex", gap: "24px", alignItems: "center" , justifyContent: "space-between",}}>
          {/* Back Button */}
          <Button
            onClick={handleBack}
            variant="outlined"
            disabled={isLoading}
            sx={{
              width: "120px",
              height: "40px",
              borderRadius: "8px",
              borderWidth: "2px",
              padding: "8px 16px",
              borderColor: "#0094FB",
              color: "#0094FB",
              textTransform: "none",
              fontFamily: "Manrope",
              fontWeight: 700,
              fontSize: "20px",
              lineHeight: "24px",
              letterSpacing: "0.15px",
              "&:hover": {
                borderColor: "#0094FB",
                backgroundColor: "rgba(0, 148, 251, 0.04)",
              },
              "&:disabled": {
                borderColor: "#ccc",
                color: "#999",
              },
            }}
          >
             Back
          </Button>

          {/* Confirm Button */}
          <Button
            onClick={handleConfirm}
            variant="contained"
            disabled={isLoading}
            sx={{
              width: "200px",
              height: "40px",
              borderRadius: "8px",
              borderWidth: "2px",
              padding: "8px 16px",
              backgroundColor: "#0094FB",
              color: "white",
              textTransform: "none",
              fontFamily: "Manrope",
              fontWeight: 700,
              fontSize: "20px",
              lineHeight: "24px",
              letterSpacing: "0.15px",
              "&:hover": {
                backgroundColor: "#0078D4",
              },
              "&:disabled": {
                backgroundColor: "#ccc",
                color: "#999",
              },
            }}
          >
            {isLoading ? "Pushing..." : "Confirm "}
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
}

export default GitHubConfigDrawer;
